/**
 * @file ContestWorkspace.jsx
 * @module ui/src/components/admin/ContestWorkspace.jsx
 * @description
 *   C-67 — Contest Program Workspace. Runtime instrument surface.
 *   EXECUTE → Programs & Adjustments → Contests.
 *
 *   6 tabs: Overview · Prizes · Leaderboard · Results · Approval · Activity
 *
 *   S925 — New component. Replaces <ComingSoonSection label="Contests" /> in
 *   ProgramsAndAdjustmentsHub.jsx. Backend: p4_contest_router.py (all 9 routes
 *   live, AD-S924-02). Do NOT build a new contest backend router.
 *
 *   Architecture:
 *   - Standalone runtime instrument — does NOT use usePlanDocument,
 *     usePlanGovernance, or usePlanValidation (design-time hooks).
 *   - No custom hook needed — contest state is simpler than SPIFF.
 *     Direct fetch pattern with AbortController + mounted ref (same as
 *     useSpiffProgram.js pattern but inlined).
 *   - Contest scopes at tenant + fiscal_period_id (AD-119, no plan_component_id).
 *   - Status machine: draft → open → active → closed → awarded (forward-only).
 *   - Approval: must be in 'open' status. SoD: created_by ≠ approved_by (NN-10).
 *   - Leaderboard + Results are engine-written, read-only (§66.8, §66.9).
 *   - Activity panel: entity_type='contest', entity_id=contest_id (uuid).
 *     Correct for contest (uuid PK). entity_id filter added to audit_log_router
 *     S925 (OI-S924-AUDIT-LOG-INT-FILTER CLOSED).
 *   - Inline panels (OI-S813-MODIFIER-SUBCOMPONENT-FILES pattern):
 *     ContestPrizesPanel, ContestLeaderboardPanel, ContestResultsPanel,
 *     ContestApprovalPanel, ContestActivityPanel.
 *     Refactor to separate files when any panel exceeds ~150L.
 *
 *   CSS: index.css + tokens.css. All colours via rgb(var(--color-*)).
 *   Button classes: .primary-button, .secondary-button, .icon-button, .text-button
 *   Table classes: .modifier-manager-table-wrapper, .modifier-manager-table
 *   Badge classes: .modifier-manager-badge--*
 *
 *   UI directives (S925, carry from S924):
 *   D-1: Tables that grow long → scrollable + sortable + searchable + clickable
 *   D-2: Long screens → collapsible sections (secondary collapsed by default)
 *
 *   Routes wired (p4_contest_router.py):
 *   §66.1  POST   /contests                        — create
 *   §66.2  PATCH  /contests/{id}                   — patch (draft only)
 *   §66.3  POST   /contests/{id}/status            — status transition
 *   §66.4  POST   /contests/{id}/approve           — approve (open status only)
 *   §66.5  GET    /contests                        — list
 *   §66.6  POST   /contest-prizes                  — add prize tier
 *   §66.7  GET    /contests/{id}/prizes            — list prizes
 *   §66.8  GET    /contest-leaderboard             — leaderboard (engine)
 *   §66.9  GET    /contest-award-results           — award results (engine)
 *
 * @author data-fit LLC · DecisionFlow · S925
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'overview',    label: 'Overview' },
  { key: 'prizes',      label: 'Prizes' },
  { key: 'leaderboard', label: 'Leaderboard' },
  { key: 'results',     label: 'Results' },
  { key: 'approval',    label: 'Approval' },
  { key: 'activity',    label: 'Activity' },
];

// Forward-only status machine (§66.3)
const STATUS_TRANSITIONS = {
  draft:   'open',
  open:    'active',
  active:  'closed',
  closed:  'awarded',
};

const STATUS_BADGE = {
  draft:   'modifier-manager-badge--not-created',
  open:    'modifier-manager-badge--warning',
  active:  'modifier-manager-badge--complete',
  closed:  'modifier-manager-badge--readonly',
  awarded: 'modifier-manager-badge--complete',
};

const STATUS_LABEL = {
  draft:   'Draft',
  open:    'Open — awaiting approval',
  active:  'Active',
  closed:  'Closed',
  awarded: 'Awarded',
};

const RANK_METRIC_LABELS = {
  attainment_pct: 'Attainment %',
  credit_total:   'Credit Total',
  margin_total:   'Margin Total',  // DDL r63 CHECK: attainment_pct|credit_total|margin_total. units_sold is NOT in CHECK — BUG-2 fix S884.
};

const TIE_BREAK_LABELS = {
  earlier_close_date: 'Earlier close date',
  higher_credit:      'Higher credit',
  alphabetical_id:    'Alphabetical ID',
};

const ELIGIBILITY_LABELS = {
  pod:           'Pod',
  role:          'Role',
  segment:       'Segment',
  business_unit: 'Business Unit',
  custom:        'Custom',
};

const PRIZE_TYPE_LABELS = {
  cash:         'Cash',
  non_cash:     'Non-Cash Award',
  spiff_award:  'SPIFF Award',
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function _iso(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function _date(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
}

function _fmt(amount, currency) {
  if (amount == null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return String(amount);
  return `${currency || ''} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
}

async function apiFetch(url, opts = {}) {
  const r = await fetch(url, { credentials: 'include', ...opts });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    const err = new Error(b?.error?.message || b?.detail || `Request failed (${r.status})`);
    err.status = r.status;
    err.errorCode = b?.error?.code ?? null;
    throw err;
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// S928 — OI-S920-403-APPROVAL-ERROR-NOT-SURFACED
// Shared SoD 403 error classifier for all approval actions in this workspace.
// Returns an actionable message string; caller sets its own error state.
// Detects by err.status (preferred — attached by apiFetch above) or by
// error code / message keywords as fallback.
// ---------------------------------------------------------------------------
function handleApprovalError(err) {
  const is403 = err.status === 403;
  const isSoD = is403
    || (err.errorCode && err.errorCode.toLowerCase().includes('segregation'))
    || (err.message  && (
         err.message.toLowerCase().includes('segregation') ||
         err.message.toLowerCase().includes('cannot approve') ||
         err.message.toLowerCase().includes('created this') ||
         err.message.toLowerCase().includes('sod') ||
         err.message.toLowerCase().includes('separation of duties')
       ));
  if (isSoD) {
    return 'You cannot approve this — Separation of Duties (NN-10) prevents the creator from approving their own record. A different comp admin must perform this action.';
  }
  return err.message || 'An unexpected error occurred.';
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function Field({ label, hint, error, children, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>
          {label}{required && <span style={{ color: 'rgb(var(--color-danger))' }}> *</span>}
        </label>
      )}
      {children}
      {hint && !error && <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', marginTop: 2 }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: 'rgb(var(--color-danger))', marginTop: 2 }} role="alert">{error}</span>}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'rgb(var(--color-text-muted))', minWidth: 200 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{value ?? '—'}</span>
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: open ? '1px solid rgb(var(--color-border))' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
          {badge && <span className={`modifier-manager-badge ${badge.cls}`}>{badge.label}</span>}
        </div>
        <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

const S = {
  input: {
    border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '6px 10px',
    fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
    width: '100%', boxSizing: 'border-box',
  },
  select: {
    border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '6px 10px',
    fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
    width: '100%', boxSizing: 'border-box',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
};

// ===========================================================================
// TAB 1 — OVERVIEW
// ===========================================================================
function OverviewTab({ contest, onStatusTransition, transitionLoading, transitionError }) {
  if (!contest) return null;

  const status = contest.contest_status;
  const nextStatus = STATUS_TRANSITIONS[status];
  const canTransition = !!nextStatus && status !== 'open'; // 'open → active' requires approval first
  const needsApprovalFirst = status === 'open';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <CollapsibleSection title="Contest Summary" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Contest name"     value={contest.contest_name} />
          <InfoRow label="Contest code"     value={contest.contest_code} />
          <InfoRow label="Fiscal period"    value={contest.fiscal_period_id} />
          <InfoRow label="Date range"       value={`${_date(contest.start_date)} → ${_date(contest.end_date)}`} />
          <InfoRow label="Eligibility"      value={ELIGIBILITY_LABELS[contest.eligibility_type] ?? contest.eligibility_type} />
          <InfoRow label="Rank metric"      value={RANK_METRIC_LABELS[contest.rank_metric] ?? contest.rank_metric} />
          <InfoRow label="Tie-break rule"   value={TIE_BREAK_LABELS[contest.tie_break_rule] ?? contest.tie_break_rule} />
          <InfoRow label="Created"          value={_iso(contest.created_at)} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Policy Envelope" defaultOpen
        badge={contest.max_individual_award || contest.finance_approval_threshold
          ? { cls: 'modifier-manager-badge--complete', label: 'Configured' }
          : { cls: 'modifier-manager-badge--not-created', label: 'No caps set' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Max individual award"    value={contest.max_individual_award ? _fmt(contest.max_individual_award, null) : 'No cap'} />
          <InfoRow label="Max duration"            value={contest.max_duration_days ? `${contest.max_duration_days} days` : 'No limit'} />
          <InfoRow label="Finance gate threshold"  value={contest.finance_approval_threshold ? _fmt(contest.finance_approval_threshold, null) : 'Not required'} />
          <InfoRow label="Permitted payout forms"  value={contest.permitted_payout_forms?.length ? contest.permitted_payout_forms.join(', ') : 'All forms permitted'} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Approval & Engine Gate" defaultOpen={false}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Submitted by"  value={contest.submitted_by ?? '—'} />
          <InfoRow label="Submitted at"  value={_iso(contest.submitted_at)} />
          <InfoRow label="Approved by"   value={contest.approved_by ?? '—'} />
          <InfoRow label="Approved at"   value={_iso(contest.approved_at)} />
          <InfoRow label="G-55 gate"
            value={contest.approved_by
              ? '✓ SATISFIED — approved_by IS NOT NULL. Active contests are included in the next calculation run.'
              : '⏳ PENDING — approve this contest to allow engine inclusion.'} />
        </div>
      </CollapsibleSection>

      {/* Status transition */}
      <CollapsibleSection title="Status" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>Current status:</span>
            <span className={`modifier-manager-badge ${STATUS_BADGE[status] ?? ''}`}>
              {STATUS_LABEL[status] ?? status}
            </span>
          </div>

          {/* Status machine flow display */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {['draft', 'open', 'active', 'closed', 'awarded'].map((s, i, arr) => (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                  background: s === status ? 'rgb(var(--color-primary))' : 'rgb(var(--color-surface))',
                  color: s === status ? '#fff' : 'rgb(var(--color-text-muted))',
                  border: `1px solid ${s === status ? 'rgb(var(--color-primary))' : 'rgb(var(--color-border))'}`,
                }}>
                  {STATUS_LABEL[s] ?? s}
                </span>
                {i < arr.length - 1 && <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>→</span>}
              </span>
            ))}
          </div>

          {transitionError && (
            <div className="modifier-manager-error-state" style={{ padding: '8px 12px' }}>
              <p style={{ margin: 0 }}>{transitionError}</p>
            </div>
          )}

          {needsApprovalFirst && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12, color: 'rgb(var(--color-text))' }}>
              Contest must be approved (Approval tab) before it can be moved to Active.
            </div>
          )}

          {canTransition && nextStatus && (
            <div>
              <button
                type="button"
                className="secondary-button"
                disabled={transitionLoading}
                onClick={() => onStatusTransition(nextStatus)}
              >
                {transitionLoading ? 'Updating…' : `Move to ${STATUS_LABEL[nextStatus] ?? nextStatus}`}
              </button>
            </div>
          )}

          {status === 'awarded' && (
            <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
              Contest is fully closed. Results are available in the Results tab.
            </div>
          )}
        </div>
      </CollapsibleSection>

    </div>
  );
}

// ===========================================================================
// TAB 2 — PRIZES
// ===========================================================================
function ContestPrizesPanel({ contestId, contestStatus }) {
  const [prizes, setPrizes]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const mountedRef = useRef(true);

  const canEdit = contestStatus === 'draft' || contestStatus === 'open';

  const [form, setForm] = useState({
    rank_from: '', rank_to: '', prize_type: 'cash',
    cash_amount: '', cash_currency: 'USD',
    non_cash_award_type_id: '', description: '',
  });

  const loadPrizes = useCallback(async () => {
    if (!contestId) return;
    setLoading(true); setError(null);
    try {
      const d = await apiFetch(`${API_BASE}/contests/${contestId}/prizes`);
      if (mountedRef.current) setPrizes(d.items ?? []);
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [contestId]);

  useEffect(() => {
    mountedRef.current = true;
    loadPrizes();
    return () => { mountedRef.current = false; };
  }, [loadPrizes]);

  const resetForm = () => setForm({
    rank_from: '', rank_to: '', prize_type: 'cash',
    cash_amount: '', cash_currency: 'USD',
    non_cash_award_type_id: '', description: '',
  });

  async function handleAddPrize() {
    setSaving(true); setSaveError(null);
    try {
      const body = {
        contest_id: contestId,
        rank_from: parseInt(form.rank_from, 10),
        rank_to: parseInt(form.rank_to, 10),
        prize_type: form.prize_type,
        cash_amount: form.prize_type === 'cash' ? form.cash_amount : null,
        cash_currency: form.prize_type === 'cash' ? form.cash_currency : null,
        non_cash_award_type_id: form.prize_type === 'non_cash' ? form.non_cash_award_type_id || null : null,
        description: form.description || null,
      };
      await apiFetch(`${API_BASE}/contest-prizes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      resetForm();
      setShowForm(false);
      await loadPrizes();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="modifier-manager-loading">Loading prizes…</div>;
  if (error)   return <div className="modifier-manager-error-state"><h4>Failed to load prizes</h4><p>{error}</p><button type="button" className="secondary-button" onClick={loadPrizes}>Retry</button></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {prizes.length === 0 && !showForm ? (
        <div className="modifier-manager-empty-state">
          <h4>No prize tiers defined</h4>
          <p>Add rank-banded prizes to this contest. Prizes can be cash, non-cash awards, or SPIFF awards.</p>
          {canEdit && (
            <button type="button" className="primary-button" onClick={() => setShowForm(true)}>
              + Add first prize tier
            </button>
          )}
        </div>
      ) : (
        <>
          {prizes.length > 0 && (
            <div className="modifier-manager-table-wrapper">
              <table className="modifier-manager-table">
                <thead>
                  <tr>
                    <th>Rank range</th>
                    <th>Prize type</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {prizes.map(p => (
                    <tr key={p.prize_id}>
                      <td className="modifier-manager-band-number">
                        {p.rank_from === p.rank_to ? `#${p.rank_from}` : `#${p.rank_from}–${p.rank_to}`}
                      </td>
                      <td>{PRIZE_TYPE_LABELS[p.prize_type] ?? p.prize_type}</td>
                      <td>
                        {p.prize_type === 'cash'
                          ? _fmt(p.cash_amount, p.cash_currency)
                          : p.prize_type === 'non_cash'
                          ? (p.non_cash_award_type_id ?? '—')
                          : 'SPIFF award'}
                      </td>
                      <td style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{p.description ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canEdit && !showForm && (
            <div>
              <button type="button" className="secondary-button" onClick={() => setShowForm(true)}>
                + Add prize tier
              </button>
            </div>
          )}
        </>
      )}

      {/* Add prize form */}
      {showForm && canEdit && (
        <CollapsibleSection title="New prize tier" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={S.grid3}>
              <Field label="Rank from" required>
                <input type="number" style={S.input} min="1" value={form.rank_from}
                  onChange={e => setForm(f => ({ ...f, rank_from: e.target.value }))} placeholder="e.g. 1" />
              </Field>
              <Field label="Rank to" required hint="Same as rank from for a single rank">
                <input type="number" style={S.input} min="1" value={form.rank_to}
                  onChange={e => setForm(f => ({ ...f, rank_to: e.target.value }))} placeholder="e.g. 3" />
              </Field>
              <Field label="Prize type" required>
                <select style={S.select} value={form.prize_type}
                  onChange={e => setForm(f => ({ ...f, prize_type: e.target.value }))}>
                  <option value="cash">Cash</option>
                  <option value="non_cash">Non-Cash Award</option>
                  <option value="spiff_award">SPIFF Award</option>
                </select>
              </Field>
            </div>

            {form.prize_type === 'cash' && (
              <div style={S.grid2}>
                <Field label="Cash amount" required>
                  <input type="number" style={S.input} min="0" step="0.01" value={form.cash_amount}
                    onChange={e => setForm(f => ({ ...f, cash_amount: e.target.value }))} placeholder="e.g. 1000" />
                </Field>
                <Field label="Currency" required>
                  <input type="text" style={S.input} value={form.cash_currency} maxLength={3}
                    onChange={e => setForm(f => ({ ...f, cash_currency: e.target.value.toUpperCase() }))} placeholder="USD" />
                </Field>
              </div>
            )}

            {form.prize_type === 'non_cash' && (
              <Field label="Non-cash award type ID" required hint="UUID from non_cash_award_type table">
                <input type="text" style={S.input} value={form.non_cash_award_type_id}
                  onChange={e => setForm(f => ({ ...f, non_cash_award_type_id: e.target.value }))} placeholder="UUID" />
              </Field>
            )}

            <Field label="Description" hint="Optional — displayed to reps on leaderboard">
              <input type="text" style={S.input} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Winner's circle trip" />
            </Field>

            {saveError && <div className="modifier-manager-error-state"><p style={{ margin: 0 }}>{saveError}</p></div>}

            <div className="modifier-manager-footer">
              <span />
              <div className="modifier-manager-footer-actions">
                <button type="button" className="secondary-button" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</button>
                <button type="button" className="primary-button"
                  onClick={handleAddPrize}
                  disabled={saving || !form.rank_from || !form.rank_to ||
                    (form.prize_type === 'cash' && (!form.cash_amount || !form.cash_currency)) ||
                    (form.prize_type === 'non_cash' && !form.non_cash_award_type_id)}>
                  {saving ? 'Adding…' : 'Add prize tier'}
                </button>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {!canEdit && prizes.length > 0 && (
        <div className="modifier-manager-readonly-note">
          Prize structure is locked once the contest moves past Open status.
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 3 — LEADERBOARD
// ===========================================================================
function ContestLeaderboardPanel({ contestId }) {
  const [runId, setRunId]       = useState('');
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [isFinal, setIsFinal]   = useState(null);

  async function loadLeaderboard() {
    if (!runId.trim()) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        contest_id: contestId,
        calculation_run_id: runId.trim(),
        limit: '200',
      });
      if (isFinal !== null) params.set('is_final', String(isFinal));
      const d = await apiFetch(`${API_BASE}/contest-leaderboard?${params}`);
      setRows(d.items ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Calculation run ID" hint="UUID from a completed calculation run">
          <input type="text" style={{ ...S.input, width: 320 }} value={runId}
            onChange={e => setRunId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </Field>
        <Field label="Final only">
          <select style={{ ...S.select, width: 140 }} value={isFinal === null ? '' : String(isFinal)}
            onChange={e => setIsFinal(e.target.value === '' ? null : e.target.value === 'true')}>
            <option value="">All runs</option>
            <option value="true">Final only</option>
            <option value="false">Interim only</option>
          </select>
        </Field>
        <button type="button" className="primary-button" onClick={loadLeaderboard} disabled={loading || !runId.trim()}>
          {loading ? 'Loading…' : 'Load leaderboard'}
        </button>
      </div>

      {error && <div className="modifier-manager-error-state"><p style={{ margin: 0 }}>{error}</p></div>}

      {rows.length === 0 && !loading && !error && (
        <div className="modifier-manager-empty-state">
          <h4>No leaderboard data</h4>
          <p>Enter a calculation run ID to view standings. Leaderboard is engine-written — run a calculation first.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}>Rank</th>
                <th>Position ID</th>
                <th>Metric value</th>
                <th>Cohort size</th>
                <th>Final</th>
                <th>Visible to rep</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.leaderboard_id}>
                  <td className="modifier-manager-band-number">#{r.rank_position}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.position_id}</td>
                  <td>{r.metric_value != null ? Number(r.metric_value).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}</td>
                  <td>{r.cohort_size}</td>
                  <td>{r.is_final ? '✓' : '—'}</td>
                  <td>{r.is_visible_to_rep ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', padding: '8px 0' }}>
            {rows.length} positions · Engine-written, read-only (§66.8)
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 4 — RESULTS
// ===========================================================================
function ContestResultsPanel({ contestId }) {
  const [runId, setRunId]     = useState('');
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  async function loadResults() {
    if (!runId.trim()) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        calculation_run_id: runId.trim(),
        contest_id: contestId,
        limit: '200',
      });
      const d = await apiFetch(`${API_BASE}/contest-award-results?${params}`);
      setRows(d.items ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <Field label="Calculation run ID" hint="UUID from a final calculation run">
          <input type="text" style={{ ...S.input, width: 320 }} value={runId}
            onChange={e => setRunId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </Field>
        <button type="button" className="primary-button" onClick={loadResults} disabled={loading || !runId.trim()}>
          {loading ? 'Loading…' : 'Load results'}
        </button>
      </div>

      {error && <div className="modifier-manager-error-state"><p style={{ margin: 0 }}>{error}</p></div>}

      {rows.length === 0 && !loading && !error && (
        <div className="modifier-manager-empty-state">
          <h4>No award results</h4>
          <p>Award results are engine-written. Enter a final calculation run ID to view awarded positions.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th>Final rank</th>
                <th>Position ID</th>
                <th>Prize type</th>
                <th>Cash amount</th>
                <th>Prize ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.award_result_id}>
                  <td className="modifier-manager-band-number">#{r.final_rank}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.position_id}</td>
                  <td>{PRIZE_TYPE_LABELS[r.prize_type] ?? r.prize_type}</td>
                  <td>{r.cash_amount != null ? _fmt(r.cash_amount, null) : '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.prize_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', padding: '8px 0' }}>
            {rows.length} awarded positions · Engine-written, read-only (§66.9)
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 5 — APPROVAL
// ===========================================================================
function ContestApprovalPanel({ contest, contestId, currentUser, onActionComplete }) {
  const [approving, setApproving]         = useState(false);
  const [approveError, setApproveError]   = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [transError, setTransError]       = useState(null);

  if (!contest) return null;

  const status = contest.contest_status;
  const isSelf = currentUser?.user_id && contest.created_by
    ? String(currentUser.user_id) === String(contest.created_by)
    : false;

  // §66.3: submit = draft → open
  async function handleSubmit() {
    setTransitioning(true); setTransError(null);
    try {
      await apiFetch(`${API_BASE}/contests/${contestId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_status: 'open' }),
      });
      onActionComplete?.();
    } catch (e) {
      setTransError(handleApprovalError(e));
    } finally {
      setTransitioning(false);
    }
  }

  // §66.4: approve = must be in open status, different user
  async function handleApprove() {
    setApproving(true); setApproveError(null);
    try {
      await apiFetch(`${API_BASE}/contests/${contestId}/approve`, { method: 'POST' });
      onActionComplete?.();
    } catch (e) {
      setApproveError(handleApprovalError(e));
    } finally {
      setApproving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Current approval state */}
      <CollapsibleSection title="Approval Status" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Contest status"  value={<span className={`modifier-manager-badge ${STATUS_BADGE[status] ?? ''}`}>{STATUS_LABEL[status] ?? status}</span>} />
          <InfoRow label="Submitted by"    value={contest.submitted_by ?? '—'} />
          <InfoRow label="Submitted at"    value={_iso(contest.submitted_at)} />
          <InfoRow label="Approved by"     value={contest.approved_by ?? '—'} />
          <InfoRow label="Approved at"     value={_iso(contest.approved_at)} />
        </div>
      </CollapsibleSection>

      {/* SoD rules reminder */}
      <CollapsibleSection title="Separation of Duties" defaultOpen={false}>
        <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 8px' }}>NN-10: The user who created this contest cannot approve it.</p>
          <p style={{ margin: 0 }}>G-55: Contest must have approved_by IS NOT NULL before it can transition to Active status.</p>
        </div>
      </CollapsibleSection>

      {/* Action area */}
      {status === 'draft' && (
        <div style={{ padding: 16, border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))' }}>
          <p style={{ fontSize: 13, margin: '0 0 12px', color: 'rgb(var(--color-text))' }}>
            Submit this contest for CA-2 approval. Once submitted, the contest moves to Open status and can be approved by a different user.
          </p>
          {transError && <div className="modifier-manager-error-state" style={{ marginBottom: 12 }}><p style={{ margin: 0 }}>{transError}</p></div>}
          <button type="button" className="primary-button" onClick={handleSubmit} disabled={transitioning}>
            {transitioning ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      )}

      {status === 'open' && (
        <div style={{ padding: 16, border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))' }}>
          {isSelf ? (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12, color: 'rgb(var(--color-text))' }}>
              You created this contest and cannot approve it (NN-10). Another comp_admin must approve.
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, margin: '0 0 12px', color: 'rgb(var(--color-text))' }}>
                Approve this contest (CA-2). Satisfies G-55 — the contest can then be moved to Active status and will be picked up by the calculation engine.
              </p>
              {approveError && <div className="modifier-manager-error-state" style={{ marginBottom: 12 }}><p style={{ margin: 0 }}>{approveError}</p></div>}
              <button type="button" className="primary-button" onClick={handleApprove} disabled={approving}>
                {approving ? 'Approving…' : 'Approve contest (CA-2)'}
              </button>
            </>
          )}
        </div>
      )}

      {(status === 'approved' || status === 'active' || status === 'closed' || status === 'awarded') && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-success) / 0.07)', border: '1px solid rgb(var(--color-success) / 0.2)', fontSize: 12, color: 'rgb(var(--color-success))' }}>
          ✓ Approved. G-55 SATISFIED — engine will include this contest in calculation runs.
        </div>
      )}

    </div>
  );
}

// ===========================================================================
// TAB 6 — ACTIVITY
// ===========================================================================
function ContestActivityPanel({ contestId, contestName }) {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [hasMore, setHasMore]   = useState(false);
  const [cursor, setCursor]     = useState(null);
  const [search, setSearch]     = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const PAGE_SIZE = 25;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!contestId) return;
    setLoading(true);

    // entity_id uuid filter — correct for contest (uuid PK).
    // audit_log_router.py entity_id param added S925 (OI-S924-AUDIT-LOG-INT-FILTER CLOSED).
    const params = new URLSearchParams({
      entity_type: 'contest',
      entity_id: contestId,
      limit: String(PAGE_SIZE),
    });
    if (cursor) params.set('cursor', cursor);

    apiFetch(`${API_BASE}/audit-logs?${params}`)
      .then(d => {
        if (!mountedRef.current) return;
        const newEntries = d.data ?? d.items ?? [];
        setEntries(prev => cursor ? [...prev, ...newEntries] : newEntries);
        setHasMore(d.pagination?.has_more ?? false);
      })
      .catch(() => {
        if (mountedRef.current) setEntries([]);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [contestId, cursor, refreshKey]);

  function loadMore() {
    if (entries.length > 0) {
      setCursor(entries[entries.length - 1].created_at);
    }
  }

  function refresh() {
    setEntries([]);
    setCursor(null);
    setRefreshKey(k => k + 1);
  }

  function eventLabel(eventType) {
    const map = {
      'p4.contest.created':            'Contest created (Draft)',
      'p4.contest.updated':            'Contest updated',
      'p4.contest.status_transitioned':'Status changed',
      'p4.contest_prize.created':      'Prize tier added',
    };
    return map[eventType] ?? eventType;
  }

  const filtered = search
    ? entries.filter(e => {
        const s = search.toLowerCase();
        return (e.event_type ?? '').toLowerCase().includes(s)
          || eventLabel(e.event_type).toLowerCase().includes(s)
          || (e.change_summary ?? '').toLowerCase().includes(s);
      })
    : entries;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>
          Activity — {contestName ?? 'Contest'}
        </span>
        <button type="button" className="icon-button" onClick={refresh} title="Refresh" aria-label="Refresh activity">↻</button>
      </div>

      <input
        style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
        placeholder="Filter activity…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {loading && entries.length === 0 && (
        <div className="modifier-manager-loading">Loading activity…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="modifier-manager-empty-state">
          <h4>No activity yet</h4>
          <p>Actions taken on this contest will appear here.</p>
        </div>
      )}

      {filtered.map((e, i) => (
        <div key={e.audit_log_id ?? i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', minWidth: 140, flexShrink: 0 }}>
            {_iso(e.created_at)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{eventLabel(e.event_type)}</span>
            {e.change_summary && (
              <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{e.change_summary}</span>
            )}
          </div>
        </div>
      ))}

      {hasMore && (
        <div style={{ paddingTop: 8 }}>
          <button type="button" className="secondary-button" disabled={loading} onClick={loadMore}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// CREATE MODE — new contest form
// ===========================================================================
function ContestCreateForm({ fiscalPeriodId, onCreated, onCancel }) {
  const [form, setForm] = useState({
    contest_name: '', contest_code: '', start_date: '', end_date: '',
    eligibility_type: 'role', rank_metric: 'attainment_pct',
    tie_break_rule: 'earlier_close_date',
    fiscal_period_id: fiscalPeriodId ?? '',
  });
  const [creating, setCreating]   = useState(false);
  const [createError, setCreateError] = useState(null);

  async function handleCreate() {
    setCreating(true); setCreateError(null);
    try {
      const body = {
        contest_name:      form.contest_name,
        contest_code:      form.contest_code || null,
        start_date:        form.start_date,
        end_date:          form.end_date,
        eligibility_type:  form.eligibility_type,
        rank_metric:       form.rank_metric,
        tie_break_rule:    form.tie_break_rule,
        fiscal_period_id:  parseInt(form.fiscal_period_id, 10),
      };
      const result = await apiFetch(`${API_BASE}/contests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      onCreated?.(result.contest_id);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  const valid = form.contest_name && form.start_date && form.end_date
    && form.eligibility_type && form.rank_metric && form.tie_break_rule
    && form.fiscal_period_id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 0 12px', borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 20 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
            EXECUTE · Programs &amp; Adjustments · Contests
          </p>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: 0 }}>New Contest</h1>
          <p style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', margin: '4px 0 0' }}>
            Scoped to tenant + fiscal period. No plan component required (AD-119).
          </p>
        </div>
        <button type="button" className="secondary-button" onClick={onCancel}>← Back</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <CollapsibleSection title="Contest Details" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={S.grid2}>
              <Field label="Contest name" required>
                <input type="text" style={S.input} value={form.contest_name}
                  onChange={e => setForm(f => ({ ...f, contest_name: e.target.value }))} placeholder="e.g. Q3 Accelerator Sprint" />
              </Field>
              <Field label="Contest code" hint="Auto-generated if blank">
                <input type="text" style={S.input} value={form.contest_code}
                  onChange={e => setForm(f => ({ ...f, contest_code: e.target.value }))} placeholder="Optional" />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Start date" required>
                <input type="date" style={S.input} value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </Field>
              <Field label="End date" required>
                <input type="date" style={S.input} value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </Field>
            </div>
            <Field label="Fiscal period ID" required>
              <input type="number" style={S.input} value={form.fiscal_period_id}
                onChange={e => setForm(f => ({ ...f, fiscal_period_id: e.target.value }))} placeholder="e.g. 12" />
            </Field>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Contest Rules" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={S.grid3}>
              <Field label="Eligibility" required>
                <select style={S.select} value={form.eligibility_type}
                  onChange={e => setForm(f => ({ ...f, eligibility_type: e.target.value }))}>
                  {Object.entries(ELIGIBILITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Rank metric" required hint="How participants are ranked">
                <select style={S.select} value={form.rank_metric}
                  onChange={e => setForm(f => ({ ...f, rank_metric: e.target.value }))}>
                  {Object.entries(RANK_METRIC_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Tie-break rule" required>
                <select style={S.select} value={form.tie_break_rule}
                  onChange={e => setForm(f => ({ ...f, tie_break_rule: e.target.value }))}>
                  {Object.entries(TIE_BREAK_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
          </div>
        </CollapsibleSection>

        {createError && <div className="modifier-manager-error-state"><h4>Failed to create</h4><p>{createError}</p></div>}

        <div className="modifier-manager-footer">
          <span className="modifier-manager-footer-status" />
          <div className="modifier-manager-footer-actions">
            <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
            <button type="button" className="primary-button" onClick={handleCreate} disabled={creating || !valid}>
              {creating ? 'Creating…' : 'Create contest'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// MAIN — ContestWorkspace (C-67)
// ===========================================================================
export default function ContestWorkspace({ contestId: initialContestId, fiscalPeriodId, currentUser, onClose, onSaved }) {
  const [activeTab, setActiveTab]       = useState('overview');
  const [contestId, setContestId]       = useState(initialContestId ?? null);
  const isCreate = !contestId;

  // Contest state
  const [contest, setContest]           = useState(null);
  const [loading, setLoading]           = useState(!isCreate);
  const [loadError, setLoadError]       = useState(null);

  // Status transition
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionError, setTransitionError]     = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadContest = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setLoadError(null);
    try {
      // §66.5 list with fiscal_period_id if available, then find by id
      // Simpler: list all and find — contests are per-period, small sets
      const params = new URLSearchParams({ limit: '200' });
      if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId));
      const d = await apiFetch(`${API_BASE}/contests?${params}`);
      const found = (d.items ?? []).find(c => c.contest_id === id);
      if (!found) throw new Error('Contest not found.');
      if (mountedRef.current) setContest(found);
    } catch (e) {
      if (mountedRef.current) setLoadError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fiscalPeriodId]);

  useEffect(() => {
    if (contestId) loadContest(contestId);
  }, [contestId, loadContest]);

  async function handleStatusTransition(targetStatus) {
    setTransitionLoading(true); setTransitionError(null);
    try {
      await apiFetch(`${API_BASE}/contests/${contestId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_status: targetStatus }),
      });
      await loadContest(contestId);
      onSaved?.();
    } catch (e) {
      setTransitionError(e.message);
    } finally {
      setTransitionLoading(false);
    }
  }

  // CREATE MODE
  if (isCreate) {
    return (
      <ContestCreateForm
        fiscalPeriodId={fiscalPeriodId}
        onCreated={(newId) => { setContestId(newId); onSaved?.(); }}
        onCancel={onClose}
      />
    );
  }

  // LOADING
  if (loading && !contest) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="skeleton" style={{ height: 24, width: 300, margin: '0 auto 12px' }} />
        <div className="skeleton" style={{ height: 16, width: 200, margin: '0 auto' }} />
      </div>
    );
  }

  // ERROR
  if (loadError) {
    return (
      <div className="modifier-manager-error-state" style={{ margin: 20 }}>
        <h4>Failed to load contest</h4>
        <p>{loadError}</p>
        <button type="button" className="secondary-button" onClick={() => loadContest(contestId)}>Retry</button>
      </div>
    );
  }

  const status = contest?.contest_status;
  const statusBadgeCls = STATUS_BADGE[status] ?? '';

  // WORKSPACE (existing contest)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 0 12px', borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 0 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
            EXECUTE · Programs &amp; Adjustments · Contests
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: 0 }}>
              {contest?.contest_name ?? 'Loading…'}
            </h1>
            {contest?.contest_code && (
              <code className="modifier-manager-code">{contest.contest_code}</code>
            )}
            <span className={`modifier-manager-badge ${statusBadgeCls}`}>
              {STATUS_LABEL[status] ?? status}
            </span>
          </div>
          {contest && (
            <p style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', margin: '4px 0 0' }}>
              {RANK_METRIC_LABELS[contest.rank_metric]} · {_date(contest.start_date)} → {_date(contest.end_date)} · {ELIGIBILITY_LABELS[contest.eligibility_type]}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="icon-button" onClick={() => loadContest(contestId)} title="Refresh" aria-label="Refresh contest">↻</button>
          <button type="button" className="secondary-button" onClick={onClose}>← Back</button>
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px', fontSize: 13,
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-text-muted))',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid rgb(var(--color-primary))' : '2px solid transparent',
              cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
              marginBottom: -1,
            }}
          >
            {tab.label}
            {/* Pending attention indicator */}
            {tab.key === 'approval' && status === 'open' && !contest?.approved_by && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, background: 'rgb(var(--color-warning) / 0.15)', color: 'rgb(var(--color-warning))' }}>!</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ────────────────────────────────────────────────── */}
      <div style={{ minHeight: 400 }}>
        {activeTab === 'overview' && (
          <OverviewTab
            contest={contest}
            onStatusTransition={handleStatusTransition}
            transitionLoading={transitionLoading}
            transitionError={transitionError}
          />
        )}

        {activeTab === 'prizes' && (
          <ContestPrizesPanel
            contestId={contestId}
            contestStatus={status}
          />
        )}

        {activeTab === 'leaderboard' && (
          <ContestLeaderboardPanel contestId={contestId} />
        )}

        {activeTab === 'results' && (
          <ContestResultsPanel contestId={contestId} />
        )}

        {activeTab === 'approval' && (
          <ContestApprovalPanel
            contest={contest}
            contestId={contestId}
            currentUser={currentUser}
            onActionComplete={() => { loadContest(contestId); onSaved?.(); }}
          />
        )}

        {activeTab === 'activity' && (
          <ContestActivityPanel
            contestId={contestId}
            contestName={contest?.contest_name}
          />
        )}
      </div>
    </div>
  );
}
